import {
  BrowserProvider,
  JsonRpcProvider,
  Contract,
  WebSocketProvider,
} from "ethers";
import {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
} from "react";
import {
  useWeb3Modal,
  useDisconnect,
  useWeb3ModalProvider,
  useWeb3ModalAccount,
  useWeb3ModalError,
  useSwitchNetwork,
  useWalletInfo,
} from "@web3modal/ethers/react";

import Ticket from "../abis/Ticket.json";
import VRF from "../abis/VRFCoordinatorV2_5Mock.json";

const initialUnSignerVars = {
  provider: null,
  socketProvider: null,
  socketSubId: null,
  ticketAbi: null,
  contract: null,
};

const initialSignerVars = {
  provider: null,
  signer: null,
  contract: null,
  isOwner: false,
  isAdmin: false,
};

const WalletContext = createContext();

export const WalletContextProvider = ({ children }) => {
  const { open } = useWeb3Modal();
  const { disconnect } = useDisconnect();
  const { address, chainId, isConnected } = useWeb3ModalAccount();
  const { walletProvider } = useWeb3ModalProvider();
  const { error } = useWeb3ModalError();
  const { switchNetwork } = useSwitchNetwork();
  const { walletInfo } = useWalletInfo();

  const [errorMessage, setErrorMessage] = useState(error && error);
  const clearError = () => setErrorMessage("");

  const [signerVars, setSignerVars] = useState(initialSignerVars);
  const [unSignerVars, setUnSignerVars] = useState(initialUnSignerVars);
  const [mockVRF, setMockVRF] = useState(null);
  const [fragments, setFragments] = useState([]);

  const connectContract = async () => {
    try {
      // const _socketProvider = new WebSocketProvider(
      //   `${process.env.REACT_APP_NETWORKRPCSOCKET}`
      // );
      // const _subscriptionId = await _socketProvider.send("eth_subscribe", [
      //   "logs",
      //   {
      //     address: process.env.REACT_APP_CONTRACT_ADDRESS,
      //     // "topics": ["0x56a24d85fda2978a12415e4db13d8170a2e3c40e8864de29ebc210f6428569c5"]
      //   },
      // ]);
      // console.log("Subscribed with ID:", _subscriptionId);

      // set unsigner Contract
      const jsonRpcProvider = new JsonRpcProvider(
        `${process.env.REACT_APP_NETWORKRPC}`
      );
      const _unSignerContract = new Contract(
        process.env.REACT_APP_CONTRACT_ADDRESS,
        Ticket.abi,
        jsonRpcProvider
      );

      // if (_unSignerContract && _subscriptionId) {
      if (_unSignerContract) {
        setUnSignerVars({
          provider: jsonRpcProvider,
          // socketProvider: _socketProvider,
          // socketSubId: _subscriptionId,
          ticketAbi: Ticket,
          contract: _unSignerContract,
        });
      }

      _unSignerContract?.interface.forEachEvent((item) =>
        setFragments((oldValues) => [...oldValues, item])
      );
      _unSignerContract?.interface.forEachError((item) =>
        setFragments((oldValues) => [...oldValues, item])
      );
      _unSignerContract?.interface.forEachFunction((item) =>
        setFragments((oldValues) => [...oldValues, item])
      );

      // set Contract
      if (isConnected && address) {
        const provider = new BrowserProvider(walletProvider);
        const signer = await provider.getSigner();
        const _contract = new Contract(
          process.env.REACT_APP_CONTRACT_ADDRESS,
          Ticket.abi,
          signer
        );

        if (chainId === 31337) {
          const _vrf = new Contract(
            process.env.REACT_APP_VRFCONTRACT_ADDRESS,
            VRF.abi,
            signer
          );

          if (_vrf) {
            setMockVRF(_vrf);
          }
        }

        if (signer && _contract) {
          setSignerVars({
            provider,
            signer,
            contract: _contract,
            isOwner:
              (await _contract.owner()).toLowerCase() === address.toLowerCase()
                ? true
                : false,
            isAdmin: (await _contract.admins(address)).isAuth,
          });
        }
      }

      clearError();
    } catch (err) {
      console.log("useWalletConnection:", err);
      setErrorMessage(err);
    }
  };

  const _loadWallet = useCallback(async () => {
    if (chainId !== Number(process.env.REACT_APP_NETWORKID)) {
      await switchNetwork(Number(process.env.REACT_APP_NETWORKID))
        .then(() => {
          connectContract();
        })
        .catch((err) => {
          console.log(err);
          setErrorMessage(err);
          disConnect();
        });
    } else {
      connectContract();
    }
  }, [address, chainId, isConnected]);

  const connect = async () => {
    clearError();
    open();
  };

  const disConnect = async () => {
    clearError();
    disconnect();
    setSignerVars(initialSignerVars);
  };

  const getFragment = ({name, selector, topicHash}) => {
    let res = null;
    let err = null;
      if (name) {
        res = fragments.filter(
          (item) => item.name === name
        );
      } else if (selector) {
        res = fragments.filter(
          (item) => item.selector === selector
        );
      } else if (topicHash) {
        res = fragments.filter(
          (item) => item.topicHash === topicHash
        );
      } else {
        err = "getFragment: name, selector or topicHash is required";
      }
    
    return {result: res ? res[0] : null, error: err};
  };

  useEffect(() => {
    if (error) {
      console.log("useWeb3ModalError:", error);
      setErrorMessage(`useWeb3ModalError: ${error}`);
    }
    _loadWallet();
  }, [_loadWallet]);

  return (
    <WalletContext.Provider
      value={{
        isConnected,
        connectedWalletInfo: walletInfo !== undefined && walletInfo.name,
        address,
        chainId,
        signerVars,
        unSignerVars,
        mockVRF,
        fragments,
        // error: !!errorMessage,
        errorMessage,
        clearError,
        open,
        connect,
        disConnect,
        getFragment
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};

export const useWalletConnection = () => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error(
      'useWalletConnection must be used within a "WalletContextProvider"'
    );
  }
  return context;
};
